import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle2, Timer, LayoutDashboard,
  ClipboardCheck, MoreVertical, Calendar, Eye,
  XCircle, CheckCircle, AlertTriangle, BarChart3,
  TrendingUp, TrendingDown, Percent, Activity,
  Bell, User, Settings, LogOut, Send, Award, GraduationCap,
  ChevronLeft, ChevronRight, Briefcase, Users
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { UserContext } from '../../contexts/UserContext';
import axios from 'axios';
import MagicBento from './MagicBento';
import ToolboxTalkSigningModal from '../ToolboxTalkSigningModal';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

// Utility functions
const formatDateTime = (dateTime) => {
  if (!dateTime) return 'N/A';
  return new Date(dateTime).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDate = (dateTime) => {
  if (!dateTime) return 'N/A';
  return new Date(dateTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const calculatePercentageChange = (currentValue, previousValue) => {
  if (previousValue === 0) return 0;
  return Math.round(((currentValue - previousValue) / previousValue) * 100);
};

// KPI Card component
const KPICard = ({ title, value, previousValue, icon: Icon, onClick, customContent }) => {
  const change = previousValue !== undefined ? calculatePercentageChange(value, previousValue) : null;
  const isPositive = change > 0;
  const isNegative = change < 0;

  return (
    <div
      className={`bg-white rounded-xl shadow-sm p-6 border border-steel-200 relative overflow-hidden transition-all duration-200
        ${onClick ? 'cursor-pointer hover:shadow-md hover:translate-y-[-2px]' : ''}`}
      onClick={onClick}
    >
      <div className="relative flex items-start">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center bg-steel-50`}>
          <Icon size={24} className="text-slate-500" />
        </div>

        <div className="ml-4">
          <h3 className="text-sm font-bold text-slate-600">{title}</h3>
          {customContent ? (
            customContent
          ) : (
            <>
              <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
              {change !== null && (
                <div className="flex items-center mt-2">
                  <div className={`flex items-center ${isPositive ? 'text-emerald-600' : isNegative ? 'text-rose-500' : 'text-slate-500'}`}>
                    {isPositive ? (
                      <TrendingUp size={14} className="mr-1" />
                    ) : isNegative ? (
                      <TrendingDown size={14} className="mr-1" />
                    ) : (
                      <Percent size={14} className="mr-1" />
                    )}
                    <span className="text-xs font-medium">{isPositive ? '+' : ''}{change}% from previous month</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// StatusDistribution component
const StatusDistribution = ({ data, title }) => {
  const colors = {
    'Pending Signature': '#FEE8E4',
    'Signed': '#fef3c7',
    'Approved': '#d1fae5',
    'Rejected': '#fee2e2',
    'Pending': '#FEE8E4',
    'Active': '#d1fae5'
  };
  
  const getStatusColor = (status) => colors[status] || '#f1f5f9';
  
  const getStatusIcon = (status) => {
    const icons = {
      'Pending Signature': <Clock className="h-4 w-4 text-brand-500" />,
      'Signed': <FileText className="h-4 w-4 text-amber-500" />,
      'Approved': <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
      'Rejected': <XCircle className="h-4 w-4 text-rose-500" />,
      'Pending': <Clock className="h-4 w-4 text-brand-500" />,
      'Active': <CheckCircle2 className="h-4 w-4 text-emerald-500" />
    };
    return icons[status] || <AlertTriangle className="h-4 w-4 text-slate-500" />;
  };
  
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Card className="bg-white shadow-sm border border-steel-200 rounded-xl overflow-hidden">
      <CardHeader className="pb-2 border-b-2 border-brand-500 bg-steel-50">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl font-semibold text-slate-800">{title}</CardTitle>
            <CardDescription className="mt-1 text-slate-500">
              Current status distribution
            </CardDescription>
          </div>
          <select className="text-sm border border-steel-300 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white">
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="3months">Last 3 months</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-5">
            <div className="space-y-4">
              {data.map((item, index) => (
                <div key={index} className="group">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-full" style={{ backgroundColor: getStatusColor(item.status) }}>
                        {getStatusIcon(item.status)}
                      </div>
                      <span className="text-sm font-medium text-slate-700">{item.status}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900">
                      {item.count} ({total > 0 ? (item.count / total * 100).toFixed(1) : 0}%)
                    </span>
                  </div>
                  <div className="relative h-2">
                    <div className="absolute inset-0 bg-steel-100 rounded-full" />
                    <div
                      className="absolute inset-y-0 left-0 transition-all duration-500 ease-in-out rounded-full"
                      style={{ 
                        width: `${total > 0 ? (item.count / total * 100) : 0}%`,
                        backgroundColor: getStatusColor(item.status)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ActivityTimeline component
const ActivityTimeline = ({ activities }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const displayCount = 3;
  const hasMore = activities.length > displayCount;

  const getActivityColor = (status) => {
    if (status && status.toLowerCase().includes('closed')) return '#64748b';
    if (status && status.toLowerCase().includes('approved')) return '#10b981';
    if (status && status.toLowerCase().includes('signed')) return '#f59e0b';
    if (status && status.toLowerCase().includes('pending')) return '#FE3F06';
    if (status && status.toLowerCase().includes('planned')) return '#FE3F06';
    if (status && status.toLowerCase().includes('rejected')) return '#f43f5e';
    if (status && status.toLowerCase().includes('active')) return '#10b981';
    if (status && status.toLowerCase().includes('expired')) return '#94a3b8';
    return '#64748b';
  };

  const getActivityBgColor = (status) => {
    if (status && status.toLowerCase().includes('closed')) return '#f1f5f9';
    if (status && status.toLowerCase().includes('approved')) return '#d1fae5';
    if (status && status.toLowerCase().includes('signed')) return '#fef3c7';
    if (status && status.toLowerCase().includes('pending')) return '#FEE8E4';
    if (status && status.toLowerCase().includes('planned')) return '#FEE8E4';
    if (status && status.toLowerCase().includes('rejected')) return '#fee2e2';
    if (status && status.toLowerCase().includes('active')) return '#d1fae5';
    if (status && status.toLowerCase().includes('expired')) return '#f1f5f9';
    return '#f1f5f9';
  };

  const getActivityIcon = (status, type) => {
    if (type === 'work_permit') {
      return <Briefcase size={14} className="text-brand-600" />;
    }
    return <Users size={14} className="text-brand-600" />;
  };

  const getStatusIcon = (status) => {
    if (status && status.toLowerCase().includes('closed')) {
      return <CheckCircle2 size={12} className="text-slate-500" />;
    }
    if (status && status.toLowerCase().includes('approved')) {
      return <CheckCircle2 size={12} className="text-emerald-500" />;
    }
    if (status && status.toLowerCase().includes('signed')) {
      return <FileText size={12} className="text-amber-500" />;
    }
    if (status && status.toLowerCase().includes('rejected')) {
      return <XCircle size={12} className="text-rose-500" />;
    }
    return <Clock size={12} className="text-brand-500" />;
  };

  const ActivityItem = ({ activity, compact = false }) => {
    const status = activity.status || '';
    const id = activity.id || '';
    const type = activity.type === 'work_permit' ? 'Work Permit' : 'Nomination';
    const formattedTimestamp = activity.timestamp
      ? new Date(activity.timestamp).toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';

    return (
      <div
        className={`group relative flex items-start gap-3 ${compact ? 'p-2' : 'p-3'} rounded-xl bg-steel-50 hover:bg-white hover:shadow-md border border-transparent hover:border-steel-200 transition-all duration-200 cursor-pointer`}
      >
        <div
          className={`flex-shrink-0 ${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-xl flex items-center justify-center`}
          style={{ backgroundColor: activity.type === 'work_permit' ? '#FEE8E4' : '#FEE8E4' }}
        >
          {getActivityIcon(status, activity.type)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-slate-500">{type}</span>
            <span className="text-xs text-slate-300">•</span>
            <span className="text-xs text-slate-400">{formattedTimestamp}</span>
          </div>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800 truncate">{id}</h4>
            <span
              className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full capitalize whitespace-nowrap"
              style={{
                backgroundColor: getActivityBgColor(status),
                color: getActivityColor(status)
              }}
            >
              {getStatusIcon(status)}
              {status.replace('_', ' ')}
            </span>
          </div>
          {(activity.team || activity.user) && (
            <p className="text-xs text-slate-400 mt-1 truncate">
              {activity.team ? `Team: ${activity.team}` : activity.user}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card className="bg-white shadow-sm border border-steel-200 rounded-xl h-full flex flex-col overflow-hidden">
        <CardHeader className="pb-3 border-b-2 border-brand-500 bg-steel-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl">
              <Activity size={20} className="text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-slate-800">Recent Activity</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Work permits & nominations
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 flex-1">
          {activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-400">
              <Activity size={40} className="mb-3 opacity-50" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {activities.slice(0, displayCount).map((activity, index) => (
                  <ActivityItem key={index} activity={activity} />
                ))}
              </div>
              {hasMore && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full mt-4 py-2 text-sm font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-xl transition-colors flex items-center justify-center gap-1"
                >
                  Show all ({activities.length})
                  <ChevronRight size={16} />
                </button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl">
                <Activity size={20} className="text-white" />
              </div>
              <div>
                <span className="text-lg font-semibold text-slate-800">All Recent Activity</span>
                <p className="text-xs text-slate-500 font-normal mt-0.5">{activities.length} items</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-2 mt-4">
            {activities.map((activity, index) => (
              <ActivityItem key={index} activity={activity} compact />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Expiring Certificates Chart component
const ExpiringCertificatesChart = ({ chartData }) => {
  const navigate = useNavigate();

  const labels = Object.keys(chartData || {}).sort((a, b) => {
    const weekA = parseInt(a.match(/\d+/)[0]);
    const weekB = parseInt(b.match(/\d+/)[0]);
    return weekA - weekB;
  });

  const data = {
    labels,
    datasets: [
      {
        label: 'Expiring Certificates',
        data: labels.map(key => chartData[key] || 0),
        backgroundColor: '#FE3F06',
        borderColor: '#e63805',
        borderWidth: 1,
        borderRadius: 6
      }
    ]
  };

  const handleBarClick = (event, elements, chart) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const label = chart.data.labels[index]; // e.g., "Week 25"
      const weekNumber = parseInt(label.match(/\d+/)[0]); // e.g., 25
      navigate(`/trainings/users?week=${weekNumber}`);
    }
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Expiring Certificates Over Next 4 Weeks',
        font: {
          size: 18,
          weight: '600'
        },
        color: '#1f2937'
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y} certificates`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Certificates',
          font: {
            size: 14
          },
          color: '#6b7280'
        },
        ticks: {
          precision: 0
        }
      },
      x: {
        title: {
          display: true,
          text: 'Week Number',
          font: {
            size: 14
          },
          color: '#6b7280'
        }
      }
    },
    onClick: handleBarClick
  };

  return (
    <Card className="bg-white shadow-sm border border-steel-200 rounded-xl overflow-hidden">
      <CardHeader className="pb-2 border-b-2 border-brand-500 bg-steel-50">
        <CardTitle className="text-xl font-semibold text-slate-800">Certificate Expiry Timeline</CardTitle>
        <CardDescription className="mt-1 text-slate-500">
          Number of certificates expiring in the next 4 weeks
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <Bar data={data} options={options} />
      </CardContent>
    </Card>
  );
};

// Work Permits Next 7 Days Chart component
const WorkPermitsNext7DaysChart = ({ data, userRole }) => {
  const labels = data.map(item => formatDate(item.date));
  const datasets = userRole === 'adminves' ? [
    {
      label: 'Sent',
      data: data.map(item => item.signed || 0),
      backgroundColor: '#FE3F06',
      borderColor: '#e63805',
      borderWidth: 1,
      borderRadius: 6
    },
    {
      label: 'Approved',
      data: data.map(item => item.approved || 0),
      backgroundColor: '#10b981',
      borderColor: '#059669',
      borderWidth: 1,
      borderRadius: 6
    },
    {
      label: 'Closed',
      data: data.map(item => item.closed || 0),
      backgroundColor: '#D9D9D9',
      borderColor: '#b3b3b3',
      borderWidth: 1,
      borderRadius: 6
    }
  ] : [
    {
      label: 'Planned',
      data: data.map(item => item.planned_approved || 0),
      backgroundColor: '#FE3F06',
      borderColor: '#e63805',
      borderWidth: 1,
      borderRadius: 6
    },
    {
      label: 'Signed',
      data: data.map(item => item.signed || 0),
      backgroundColor: '#f59e0b',
      borderColor: '#d97706',
      borderWidth: 1,
      borderRadius: 6
    },
    {
      label: 'Approved',
      data: data.map(item => item.approved || 0),
      backgroundColor: '#10b981',
      borderColor: '#059669',
      borderWidth: 1,
      borderRadius: 6
    },
    {
      label: 'Closed',
      data: data.map(item => item.closed || 0),
      backgroundColor: '#D9D9D9',
      borderColor: '#b3b3b3',
      borderWidth: 1,
      borderRadius: 6
    }
  ];

  const chartData = {
    labels,
    datasets
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y} permits`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Permits',
          font: {
            size: 14
          },
          color: '#6b7280'
        },
        ticks: {
          precision: 0
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 14
          },
          color: '#6b7280'
        }
      }
    }
  };

  return (
    <Card className="bg-white shadow-sm border border-steel-200 rounded-xl overflow-hidden">
      <CardHeader className="pb-2 border-b-2 border-brand-500 bg-steel-50">
        <CardTitle className="text-xl font-semibold text-slate-800">Work Permits Next 7 Days</CardTitle>
        <CardDescription className="mt-1 text-slate-500">
          Number of work permits by status for the next 7 days
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <Bar data={chartData} options={options} />
      </CardContent>
    </Card>
  );
};

// Main dashboard component
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useContext(UserContext);
  const [currentView, setCurrentView] = useState('admin');
  const [notifications, setNotifications] = useState([
    { text: "New nomination #1234 requires your approval" },
    { text: "Nomination #5678 has been signed" },
    { text: "Your recent nomination was approved" }
  ]);
  const [statsData, setStatsData] = useState({
    admin_stats: {
      expiring_certificates: {
        users_count: 0,
        total_certificates: 0,
        chart_data: {
          '0_7_days': 0,
          '8_14_days': 0,
          '15_21_days': 0,
          '22_28_days': 0
        }
      },
      nominations: {
        total: 0,
        active: 0,
        pending: 0,
        signed: 0
      },
      work_permits_today: {
        planned_approved: 0,
        signed: 0,
        approved: 0
      },
      work_permits_next_7_days: [],
      trainings: {
        upcoming_planned: 0
      }
    },
    nominations: {
      pending: 0,
      approved: 0
    },
    work_permits: {
      pending: 0,
      active: 0
    },
    previousMonth: {
      expiringCertificates: {
        users_count: 0,
        total_certificates: 0
      },
      nominations: {
        pending: 0,
        approved: 0
      },
      work_permits: {
        pending: 0,
        active: 0
      }
    }
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Checking if user is logged in
    if (!loading && !user?.accessToken) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Set current view based on path
    const path = window.location.pathname;
    setCurrentView(path.split('/')[1] || 'admin');
  }, []);

  useEffect(() => {
    // Fetch stats data
    const fetchStats = async () => {
      setDataLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/stats/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setStatsData(response.data);
        console.log("staty", response.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load statistics.');
      } finally {
        setDataLoading(false);
      }
    };
    fetchStats();
  }, []);

  const nominationStatusData = [
    { status: 'Pending', count: statsData.nominations?.pending || 0 },
    { status: 'Approved', count: statsData.nominations?.approved || 0 }
  ];

  const workPermitStatusData = [
    { status: 'Pending', count: statsData.work_permits?.pending || 0 },
    { status: 'Active', count: statsData.work_permits?.active || 0 }
  ];

  const recentActivities = statsData.admin_stats?.recent_activities || [];

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    if (status === 'approved' || status === 'active') return 'bg-emerald-100 text-emerald-800';
    if (status === 'signed') return 'bg-amber-100 text-amber-800';
    if (status === 'rejected') return 'bg-rose-100 text-rose-800';
    if (status === 'pending') return 'bg-brand-100 text-brand-800';
    return 'bg-steel-100 text-slate-800';
  };
  
  // Handle notification clicks
  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };
  
  // Handle logout
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      if (logout) {
        logout();
      }
      navigate('/login', { replace: true });
    }
  };

  // Improved loading state
  if (loading || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-steel-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg border border-steel-200 max-w-md">
          <div className="relative mx-auto w-16 h-16 mb-4">
            <div className="absolute inset-0 border-4 border-t-brand-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">Loading data</h3>
          <p className="text-slate-500">Preparing your dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-steel-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg border border-steel-200 max-w-md">
          <h3 className="text-xl font-semibold text-red-600 mb-2">Error</h3>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex-1 flex flex-col overflow-auto">
      {/* Main Dashboard Content */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-steel-50">
        <div className="container mx-auto p-4">
          {/* Dashboard header with summary */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>                
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-3">
                <div className="bg-white border border-steel-200 rounded-xl px-4 py-1.5 text-sm text-slate-600 flex items-center">
                  <Calendar size={16} className="mr-2 text-slate-400" />
                  <span>Last updated: {new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* KPI cards with MagicBento */}
          <MagicBento
            textAutoHide={false}
            enableStars={true}
            enableSpotlight={true}
            enableBorderGlow={true}
            enableTilt={false}
            enableMagnetism={true}
            clickEffect={true}
            spotlightRadius={300}
            particleCount={8}
            glowColor="254, 63, 6"
            cards={[
              {
                color: '#ffffff',
                onClick: () => navigate('/certificateexp?filter=next30'),
                content: (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start mb-2">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-steel-50">
                        <Timer size={20} className="text-slate-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xs font-bold text-slate-600 mb-1">Expiring Certificates</h3>
                      <div className="mt-1">
                        <p className="text-base font-bold text-slate-900">
                          <span className="text-amber-500">
                            {statsData.admin_stats?.expiring_certificates?.total_certificates || 0} certificates
                          </span>{' '}
                        </p>
                        <p className="text-base font-bold text-slate-900">
                          for{' '}
                          <span className="text-amber-500">
                            {statsData.admin_stats?.expiring_certificates?.users_count || 0} users
                          </span>{' '}
                        </p>
                        <p className="text-xs text-slate-600">
                          are expiring in the next 30 days
                        </p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                color: '#ffffff',
                onClick: () => navigate('/nominations/list'),
                content: (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start mb-2">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-steel-50">
                        <Award size={20} className="text-slate-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xs font-bold text-slate-600 mb-1">Nominations</h3>
                      <div className="mt-1">
                        <p className="text-base font-bold text-slate-900">
                          <span className="text-green-500">
                            {statsData.admin_stats?.nominations?.active || 0} active
                          </span>
                          {' '} and
                        </p>
                        <p className="text-base font-bold text-amber-500">
                          {(statsData.admin_stats?.nominations?.pending) + (statsData.admin_stats?.nominations?.signed) || 0} pending
                        </p>
                        <p className="text-xs text-slate-600">
                          nominations in your company
                        </p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                color: '#ffffff',
                onClick: () => navigate('/workpermits'),
                content: (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start mb-2">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-steel-50">
                        <Send size={20} className="text-slate-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xs font-bold text-slate-600 mb-1">Work Permit</h3>
                      <div className="mt-1">
                        <p className="text-base font-bold text-amber-500">
                          {user?.role === 'adminves'
                            ? `${statsData.admin_stats?.work_permits_today?.signed || 0} sent`
                            : `${((statsData.admin_stats?.work_permits_today?.planned_approved || 0) + (statsData.admin_stats?.work_permits_today?.signed || 0))} planned`} and
                        </p>
                        <p className="text-base font-bold text-green-500">
                          {(statsData.admin_stats?.work_permits_today?.approved) || 0} already accepted
                        </p>
                        <p className="text-xs text-slate-600">
                          work permits for today
                        </p>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                color: '#ffffff',
                onClick: () => navigate('/trainings'),
                content: (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start mb-2">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-steel-50">
                        <GraduationCap size={20} className="text-slate-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xs font-bold text-slate-600 mb-1">Trainings</h3>
                      <div className="mt-1">
                        <p className="text-base font-bold text-grey-500">
                          You have
                        </p>
                        <p className="text-base font-bold text-brand-500">
                          {(statsData.admin_stats?.trainings?.upcoming_planned) || 0} planned
                        </p>
                        <p className="text-xs text-slate-600">
                          trainings in your company
                        </p>
                      </div>
                    </div>
                  </div>
                )
              }
            ]}
          />
          
          {/* Main dashboard content - visualizations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <ExpiringCertificatesChart chartData={statsData.admin_stats?.expiring_certificates?.chart_data} />
            <WorkPermitsNext7DaysChart data={statsData.admin_stats?.work_permits_next_7_days || []} userRole={user?.role} />
            <ActivityTimeline activities={recentActivities} />
          </div>

        </div>
      </main>
      <ToolboxTalkSigningModal />
    </div>
  );
};

export default AdminDashboard;